import { useState, useEffect, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
	addDays,
	format,
	isToday,
	startOfWeek,
	endOfWeek,
	startOfMonth,
	endOfMonth,
} from "date-fns";
import { Button } from "@/v2/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/v2/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/v2/components/ui/tabs";
import { Calendar } from "@/v2/components/ui/calendar";

export interface DateRange {
	from: Date | undefined;
	to: Date | undefined;
}

interface DateBarProps {
	date: Date;
	dateRange: DateRange;
	onDateChange: (date: Date) => void;
	onDateRangeChange: (range: DateRange) => void;
	leftSlot?: ReactNode;
	rightSlot?: ReactNode;
}

type PickerMode = "date" | "range";

export function DateBar({ date, dateRange, onDateChange, onDateRangeChange, leftSlot, rightSlot }: DateBarProps) {
	const [open, setOpen] = useState(false);
	const isRangeActive = dateRange.from != null && dateRange.to != null;
	const [mode, setMode] = useState<PickerMode>(isRangeActive ? "range" : "date");
	const [month, setMonth] = useState(date);

	// Internal range state so we don't flash on every click
	const [pendingRange, setPendingRange] = useState<{ from?: Date; to?: Date } | undefined>(
		undefined,
	);

	// Sync internal state when popover opens
	useEffect(() => {
		if (open) {
			setMonth(dateRange.from ?? date);
			setPendingRange(
				isRangeActive ? { from: dateRange.from!, to: dateRange.to! } : undefined,
			);
			setMode(isRangeActive ? "range" : "date");
		}
	}, [open]); // eslint-disable-line react-hooks/exhaustive-deps

	const goBack = () => {
		if (isRangeActive) return;
		onDateChange(addDays(date, -1));
	};

	const goForward = () => {
		if (isRangeActive) return;
		onDateChange(addDays(date, 1));
	};

	const clearRange = () => {
		onDateRangeChange({ from: undefined, to: undefined });
	};

	const selectToday = () => {
		clearRange();
		onDateChange(new Date());
		setOpen(false);
	};

	const selectThisWeek = () => {
		const now = new Date();
		onDateRangeChange({ from: startOfWeek(now), to: endOfWeek(now) });
		setOpen(false);
	};

	const selectThisMonth = () => {
		const now = new Date();
		onDateRangeChange({ from: startOfMonth(now), to: endOfMonth(now) });
		setOpen(false);
	};

	const handleModeChange = (value: string) => {
		const newMode = value as PickerMode;
		setMode(newMode);
		if (newMode === "date") {
			setPendingRange(undefined);
			clearRange();
		}
	};

	const handleSingleSelect = (selected: Date | undefined) => {
		if (!selected) return;
		onDateChange(selected);
		setOpen(false);
	};

	const handleRangeSelect = (selected: { from?: Date; to?: Date } | undefined) => {
		if (!selected) return;
		setPendingRange(selected);
		// Only commit and close when we have a real range (two distinct dates)
		// react-day-picker sometimes fires with from === to on first click
		if (
			selected.from &&
			selected.to &&
			selected.from.getTime() !== selected.to.getTime()
		) {
			onDateRangeChange({ from: selected.from, to: selected.to });
			setOpen(false);
		}
	};

	const dateLabel = isRangeActive
		? `${format(dateRange.from!, "MMM d")} – ${format(dateRange.to!, "MMM d")}`
		: isToday(date)
			? "Today"
			: format(date, "EEE, MMM d");

	return (
		<div className="flex items-center justify-between px-2">
			{/* Left slot (filter) */}
			{leftSlot ?? <div className="w-8" />}

			{/* Date selector — center */}
			<div className="flex items-center gap-0">
				<Button
					variant="ghost"
					size="icon"
					onClick={goBack}
					disabled={isRangeActive}
					className="h-8 w-8 -mr-1.5 text-muted-foreground"
				>
					<ChevronLeft className="size-5" />
				</Button>

				<Popover open={open} onOpenChange={setOpen}>
					<PopoverTrigger asChild>
						<Button variant="ghost" className="h-8 px-1.5 text-sm font-normal">
							<span className="w-[16ch] text-center">{dateLabel}</span>
						</Button>
					</PopoverTrigger>
				<PopoverContent className="w-[280px] p-0" align="center">
					{/* Mode tabs */}
					<div className="px-3 pt-3 pb-0">
						<Tabs value={mode} onValueChange={handleModeChange}>
							<TabsList className="h-7">
								<TabsTrigger value="date" className="text-xs px-2 py-1">Date</TabsTrigger>
								<TabsTrigger value="range" className="text-xs px-2 py-1">Range</TabsTrigger>
							</TabsList>
						</Tabs>
					</div>

					{/* Calendar */}
					{mode === "date" ? (
						<Calendar
							mode="single"
							month={month}
							onMonthChange={setMonth}
							selected={date}
							onSelect={handleSingleSelect}
						/>
					) : (
						<Calendar
							mode="range"
							month={month}
							onMonthChange={setMonth}
							selected={pendingRange as any}
							onSelect={handleRangeSelect}
						/>
					)}

					{/* Quick actions */}
					<div className="border-t border-border grid grid-cols-3">
						<Button variant="ghost" className="rounded-none h-9 text-xs text-primary" onClick={selectToday}>Today</Button>
						<Button variant="ghost" className="rounded-none h-9 text-xs text-primary border-x border-border" onClick={selectThisWeek}>This week</Button>
						<Button variant="ghost" className="rounded-none h-9 text-xs text-primary" onClick={selectThisMonth}>This month</Button>
					</div>
				</PopoverContent>
			</Popover>

				<Button
					variant="ghost"
					size="icon"
					onClick={goForward}
					disabled={isRangeActive}
					className="h-8 w-8 -ml-1.5 text-muted-foreground"
				>
					<ChevronRight className="size-5" />
				</Button>
			</div>

			{/* Right slot (sort/options) */}
			{rightSlot ?? <div className="w-8" />}
		</div>
	);
}
